import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Search, MoreHorizontal, Pencil, Trash2, AlertTriangle, Eye, ArrowUpDown, ArrowUp, ArrowDown, Download, ExternalLink, Car, Home, Briefcase, FileText, Clock, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  segnalato: 'Segnalato',
  in_lavorazione: 'In Lavorazione',
  chiuso: 'Chiuso'
};

const statusColors = {
  segnalato: 'bg-blue-100 text-blue-700',
  in_lavorazione: 'bg-amber-100 text-amber-700',
  chiuso: 'bg-green-100 text-green-700'
};

const assetTypeIcons = {
  auto: Car,
  casa: Home,
  attivita: Briefcase
};

const Claims = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgentSource, setFilterAgentSource] = useState('all');
  const [filterPolicyId, setFilterPolicyId] = useState(null); // Filtro per policy_id dalla URL
  const [agentSources, setAgentSources] = useState([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Leggi policy_id dalla URL
  useEffect(() => {
    const policyIdFromUrl = searchParams.get('policy_id');
    setFilterPolicyId(policyIdFromUrl || null);
  }, [searchParams]);

  // Fetch data quando filterPolicyId cambia (incluso il primo render)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Costruisci URL con filtro policy_id se presente
        let url = `${API}/claims`;
        if (filterPolicyId) {
          url += `?policy_id=${filterPolicyId}`;
        }
        const response = await axios.get(url);
        setClaims(response.data);
      } catch (error) {
        toast.error('Errore nel caricamento dei sinistri');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    fetchAgentSources();
  }, [filterPolicyId]);

  const fetchAgentSources = async () => {
    try {
      const response = await axios.get(`${API}/agent-sources`);
      setAgentSources(response.data.map(s => s.name).filter(Boolean));
    } catch (error) {
      console.error('Error fetching agent sources:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-emerald-600" /> : 
      <ArrowDown className="w-4 h-4 text-emerald-600" />;
  };

  // Helper per generare URL di navigazione (per link nativi <a>)
  const getClientUrl = (clientId) => `/clients?open=${clientId}`;
  
  const getAssetUrl = (claim) => {
    if (claim.client_id && claim.asset_id) {
      return `/clients?open=${claim.client_id}&asset=${claim.asset_id}`;
    } else if (claim.client_id) {
      return `/clients?open=${claim.client_id}`;
    }
    return '#';
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case 'auto': return Car;
      case 'casa': return Home;
      case 'attivita': return Briefcase;
      default: return FileText;
    }
  };

  const handleStatusChange = async (claimId, newStatus) => {
    try {
      const claim = claims.find(c => c.id === claimId);
      if (!claim) return;
      
      await axios.put(`${API}/claims/${claimId}`, {
        policy_id: claim.policy_id,
        client_id: claim.client_id,
        claim_date: claim.claim_date,
        description: claim.description,
        status: newStatus
      });
      
      setClaims(prev => prev.map(c => 
        c.id === claimId ? { ...c, status: newStatus } : c
      ));
      
      toast.success('Stato aggiornato');
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (claimId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo sinistro?')) return;
    try {
      await axios.delete(`${API}/claims/${claimId}`);
      toast.success('Sinistro eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Filter and sort claims
  const filteredClaims = claims
    .filter(claim => {
      const matchesSearch = searchTerm === '' || 
        claim.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.policy_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || claim.status === filterStatus;
      const matchesAgentSource = filterAgentSource === 'all' || claim.client_agent_source === filterAgentSource;
      return matchesSearch && matchesStatus && matchesAgentSource;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'created_at' || sortField === 'claim_date') {
        aVal = new Date(a[sortField] || 0);
        bVal = new Date(b[sortField] || 0);
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }
      if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  // Pagination logic
  const totalItems = filteredClaims.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedClaims = filteredClaims.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [claims.length, searchTerm, filterStatus, filterAgentSource]);

  const exportToCSV = () => {
    const headers = ['Cliente', 'Polizza', 'Agenzia', 'Compagnia', 'Data Sinistro', 'Data Segnalazione', 'Stato', 'Descrizione'];
    const rows = filteredClaims.map(c => [
      c.client_name || '',
      c.policy_number || '',
      c.agency || '',
      c.company || '',
      formatDate(c.claim_date),
      formatDate(c.created_at),
      statusLabels[c.status] || c.status,
      c.description || ''
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sinistri_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="claims-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sinistri
          </h1>
          <p className="text-slate-600 mt-1">Gestisci i sinistri assicurativi</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-claims">
          <Download className="w-4 h-4 mr-2" />
          Esporta CSV
        </Button>
      </div>

      {/* Banner filtro polizza attivo */}
      {filterPolicyId && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Filtro attivo:</span>
              <span>Visualizzazione sinistri per polizza specifica ({claims.length} {claims.length === 1 ? 'sinistro' : 'sinistri'})</span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-orange-700 border-orange-300 hover:bg-orange-100"
              onClick={() => {
                setFilterPolicyId(null);
                setSearchParams({});
              }}
            >
              Rimuovi filtro
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per cliente, polizza, targa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-claims"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="segnalato">Segnalato</SelectItem>
                <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                <SelectItem value="chiuso">Chiuso</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAgentSource} onValueChange={setFilterAgentSource}>
              <SelectTrigger className="w-44" data-testid="filter-agent-source">
                <SelectValue placeholder="Agente/Fonte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agentSources.map(agent => (
                  <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale</p>
            <p className="text-2xl font-bold">{claims.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Segnalati</p>
            <p className="text-2xl font-bold">{claims.filter(c => c.status === 'segnalato').length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">In Lavorazione</p>
            <p className="text-2xl font-bold">{claims.filter(c => c.status === 'in_lavorazione').length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Chiusi</p>
            <p className="text-2xl font-bold">{claims.filter(c => c.status === 'chiuso').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nessun sinistro trovato
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold min-w-[180px]">Cliente</TableHead>
                    <TableHead className="font-semibold min-w-[200px]">Bene</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Agenzia</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">Compagnia</TableHead>
                    <TableHead className="font-semibold min-w-[120px]">
                      <button onClick={() => handleSort('claim_date')} className="flex items-center gap-1 hover:text-emerald-600">
                        Data Sinistro {getSortIcon('claim_date')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold min-w-[130px]">
                      <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-emerald-600">
                        Data Segnalazione {getSortIcon('created_at')}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-center min-w-[140px]">Stato</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedClaims.map((claim) => (
                    <TableRow 
                      key={claim.id} 
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => { setSelectedClaim(claim); setDetailsOpen(true); }}
                      data-testid={`claim-row-${claim.id}`}
                    >
                      <TableCell>
                        <Link
                          to={getClientUrl(claim.client_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group no-underline"
                        >
                          <span className="font-medium text-slate-900 group-hover:text-blue-600">
                            {claim.client_name || '-'}
                          </span>
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                        </Link>
                      </TableCell>
                      <TableCell className="min-w-[200px]">
                        {(() => {
                          const Icon = getAssetIcon(claim.policy_type);
                          return (
                            <Link
                              to={getAssetUrl(claim)}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors group w-full no-underline"
                            >
                              <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                                claim.policy_type === 'auto' ? 'bg-blue-100 text-blue-600' :
                                claim.policy_type === 'casa' ? 'bg-green-100 text-green-600' :
                                'bg-purple-100 text-purple-600'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate">
                                {claim.asset_label || '-'}
                              </span>
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </Link>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-slate-600">{claim.agency || '-'}</TableCell>
                      <TableCell className="text-slate-600">{claim.company || '-'}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(claim.claim_date)}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatDate(claim.created_at)}</TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={claim.status}
                          onValueChange={(value) => handleStatusChange(claim.id, value)}
                        >
                          <SelectTrigger className={`w-36 ${statusColors[claim.status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="segnalato">
                              <span className="flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                Segnalato
                              </span>
                            </SelectItem>
                            <SelectItem value="in_lavorazione">
                              <span className="flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3" />
                                In Lavorazione
                              </span>
                            </SelectItem>
                            <SelectItem value="chiuso">
                              <span className="flex items-center gap-2">
                                <CheckCircle className="w-3 h-3" />
                                Chiuso
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`claim-menu-${claim.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setSelectedClaim(claim); setDetailsOpen(true); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Dettagli
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(claim.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} sinistri
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, idx, arr) => {
                          const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                          return (
                            <div key={page} className="flex items-center">
                              {showEllipsisBefore && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-slate-900' : ''}`}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
              
              {totalPages === 1 && totalItems > 0 && (
                <div className="text-sm text-slate-500 text-center py-2 border-t border-slate-200">
                  Visualizzando {totalItems} sinistr{totalItems === 1 ? 'o' : 'i'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Dettagli Sinistro
            </DialogTitle>
          </DialogHeader>
          
          {selectedClaim && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Cliente</p>
                  <Link 
                    to={getClientUrl(selectedClaim.client_id)}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {selectedClaim.client_name}
                  </Link>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Polizza</p>
                  <p className="font-mono font-medium">
                    {selectedClaim.policy_number || <span className="text-amber-600 italic font-sans">N. Polizza da inserire</span>}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Agenzia</p>
                  <p className="font-medium">{selectedClaim.agency || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Compagnia</p>
                  <p className="font-medium">{selectedClaim.company || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Data Sinistro</p>
                  <p className="font-medium">{formatDate(selectedClaim.claim_date)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Data Segnalazione</p>
                  <p className="font-medium">{formatDate(selectedClaim.created_at)}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-500">Stato</p>
                <Badge className={statusColors[selectedClaim.status]}>
                  {statusLabels[selectedClaim.status]}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-slate-500">Descrizione</p>
                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedClaim.description}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Claims;
